import React from "react";
import { ActionIcon, Box, Drawer, Text } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import styles from "./BottomSheet.module.css";

/**
 * Bottom sheets sit BELOW Mantine's defaults (modal 200, popover / combobox
 * 300), so a modal, menu or select dropdown opened from a sheet always
 * renders above it.
 */
export const BOTTOM_SHEET_Z_INDEX = 150;

export interface BottomSheetProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  /** Replaces the title / subtitle block (e.g. a status chip + link).
   *  `title` still labels the dialog. */
  header?: React.ReactNode;
  /** Pinned below the scrolling body (e.g. Clear · Done), clear of the
   *  home indicator. */
  footer?: React.ReactNode;
  /** Name the dialog by its title (the title becomes the dialog's heading,
   *  wired with aria-labelledby). Off by default (a plain title). Needs the
   *  default title row: with `header` the title isn't rendered. */
  labelDialog?: boolean;
  /** Mantine's window-level Escape close. Off when the sheet routes Escape
   *  itself (onKeyDown), so a picker / panel closes before the sheet. */
  closeOnEscape?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  "data-testid"?: string;
}

/**
 * Phone bottom sheet over a `rgba(20,20,20,.34)` scrim: titled header with a
 * close X, scrolling body, optional pinned footer. Scrim, X and Escape all
 * close it. Promoted from the mobile calendar's sheet (same scrim, layer and
 * close paths) so Reports and other phone layouts share one sheet. The
 * calendar keeps its own MobileSheet (drag handle + slide-up motion).
 */
export const BottomSheet: React.FC<BottomSheetProps> = ({
  opened,
  onClose,
  title,
  subtitle,
  children,
  header,
  footer,
  labelDialog = false,
  closeOnEscape = true,
  onKeyDown,
  "data-testid": testId,
}) => (
  <Drawer
    opened={opened}
    onClose={onClose}
    position="bottom"
    withCloseButton={false}
    zIndex={BOTTOM_SHEET_Z_INDEX}
    closeOnEscape={closeOnEscape}
    padding={0}
    transitionProps={{ duration: 0 }}
    overlayProps={{ color: "#141414", backgroundOpacity: 0.34, blur: 0 }}
    classNames={{ content: styles.content, body: styles.body }}
    styles={{ content: { height: "auto", maxHeight: "82dvh" } }}
    // Redundant once Drawer.Title labels the dialog (labelDialog).
    aria-label={labelDialog ? undefined : title}
  >
    <Box data-testid={testId} className={styles.body} onKeyDown={onKeyDown}>
      <Box className={styles.head}>
        {header ?? (
          <Box className={styles.titles}>
            {labelDialog ? (
              <Drawer.Title className={`${styles.title} ${styles.heading}`}>
                {title}
              </Drawer.Title>
            ) : (
              <Text className={styles.title}>{title}</Text>
            )}
            {subtitle && <Text className={styles.subtitle}>{subtitle}</Text>}
          </Box>
        )}
        <ActionIcon
          variant="default"
          className={styles.close}
          onClick={onClose}
          aria-label="Close"
        >
          <IconX size={17} stroke={1.8} />
        </ActionIcon>
      </Box>
      <Box className={styles.scroll}>{children}</Box>
      {footer && <Box className={styles.footer}>{footer}</Box>}
    </Box>
  </Drawer>
);

export default BottomSheet;
